import { useState, useEffect } from 'react';
import { Button } from '@/components/Button';
import { MenuButton } from '@/components/MenuButton';
import { contactUs } from '@/utils';

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Close menu when resizing to desktop breakpoint
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768 && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMenuOpen]);

  // Prevent body scrolling when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isMenuOpen]);

  return (
    <header role="banner">
      <nav
        className="fixed left-0 right-0 top-0 z-50 flex h-20 w-full items-center justify-between bg-neutral-neutral-00 px-4 shadow-lg md:h-[100px] md:px-10"
        aria-label="Main navigation"
      >
        {/* Logo */}
        <div className="text-center font-logo text-3xl font-normal text-primary-primary-40 md:text-4xl">
          <h1 className="sr-only">Furnitech - Interior Design Services</h1>
          <span aria-label="Furnitech Home">Furnitech</span>
        </div>

        {/* Desktop Menu */}
        <div className="hidden items-center justify-center gap-6 md:flex lg:gap-8">
          <MenuButton title="Story" />
          <MenuButton title="Demo" />
          <MenuButton title="Benefits" />
          <MenuButton title="Features" />
          <MenuButton title="Cases" />
          <MenuButton title="Contacts" />
        </div>

        {/* Desktop Contact Button */}
        <div className="hidden w-auto md:block md:w-[200px]">
          <Button title="Contact us" onClick={contactUs} />
        </div>

        {/* Mobile Menu Button */}
        <button
          className="flex flex-col items-center justify-center p-2 md:hidden"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMenuOpen}
          aria-controls="mobile-menu"
        >
          <span
            className={`mb-1.5 block h-0.5 w-6 bg-primary-primary-40 transition-all duration-300 ${
              isMenuOpen ? 'translate-y-2 rotate-45' : ''
            }`}
          />
          <span
            className={`block h-0.5 w-6 bg-primary-primary-40 transition-all duration-300 ${
              isMenuOpen ? 'opacity-0' : 'opacity-100'
            }`}
          />
          <span
            className={`mt-1.5 block h-0.5 w-6 bg-primary-primary-40 transition-all duration-300 ${
              isMenuOpen ? '-translate-y-2 -rotate-45' : ''
            }`}
          />
        </button>

        {/* Mobile Menu Overlay */}
        <div
          id="mobile-menu"
          className={`fixed bottom-0 left-0 right-0 top-20 bg-neutral-neutral-00 transition-all duration-300 md:hidden ${
            isMenuOpen
              ? 'visible opacity-100'
              : 'pointer-events-none invisible opacity-0'
          }`}
          aria-hidden={!isMenuOpen}
        >
          <nav
            className="flex h-full flex-col gap-8 overflow-y-auto px-6 py-8"
            aria-label="Mobile navigation"
          >
            <MenuButton title="Story" setIsMenuOpen={setIsMenuOpen} />
            <MenuButton title="Demo" setIsMenuOpen={setIsMenuOpen} />
            <MenuButton title="Benefits" setIsMenuOpen={setIsMenuOpen} />
            <MenuButton title="Features" setIsMenuOpen={setIsMenuOpen} />
            <MenuButton title="Cases" setIsMenuOpen={setIsMenuOpen} />
            <MenuButton title="Contacts" setIsMenuOpen={setIsMenuOpen} />
            <div className="mt-4 w-full">
              <Button title="Contact us" onClick={contactUs} />
            </div>
          </nav>
        </div>
      </nav>
    </header>
  );
};
